import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  TextField,
  Fade,
} from '@mui/material';

interface InlineQuestionOptionsProps {
  options: string[];
  multipleSelection: boolean;
  onSubmit: (selectedOptions: string[], customText?: string) => Promise<void>;
  onSelectionChange: (selectedOptions: string[], customText?: string, canSubmit?: boolean) => void;
}

const InlineQuestionOptions: React.FC<InlineQuestionOptionsProps> = ({
  options,
  multipleSelection,
  onSubmit,
  onSelectionChange,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);

  const handleOptionChange = (option: string, checked: boolean) => {
    let newSelectedOptions: string[];
    
    if (multipleSelection) {
      if (checked) {
        newSelectedOptions = [...selectedOptions, option];
      } else {
        newSelectedOptions = selectedOptions.filter(opt => opt !== option);
      }
    } else {
      newSelectedOptions = [option];
    }
    
    setSelectedOptions(newSelectedOptions);

    // Show custom input if "Other" is selected
    if (option.toLowerCase() === 'other') {
      setShowCustomInput(checked);
      if (!checked) {
        setCustomText('');
      }
    }
    
    // Notify parent about selection change
    const needsCustomText = newSelectedOptions.includes('Other') && option.toLowerCase() === 'other';
    const canSubmit = newSelectedOptions.length > 0 && 
      (!needsCustomText || Boolean(customText.trim()));
    onSelectionChange(newSelectedOptions, customText, canSubmit);
  };

  const handleSingleSelection = (option: string) => {
    const newSelectedOptions = [option];
    setSelectedOptions(newSelectedOptions);
    
    // Show custom input if "Other" is selected
    if (option.toLowerCase() === 'other') {
      setShowCustomInput(true);
    } else {
      setShowCustomInput(false);
      setCustomText('');
    }
    
    // Notify parent about selection change
    const needsCustomText = option.toLowerCase() === 'other';
    const canSubmit = !needsCustomText || Boolean(customText.trim());
    onSelectionChange(newSelectedOptions, customText, canSubmit);
  };



  return (
    <Fade in={true} timeout={300}>
      <Box sx={{ mt: 2, width: '100%' }}>
        {multipleSelection ? (
          <Box>
            <Typography variant="caption" sx={{ color: '#666', mb: 2, display: 'block', fontSize: '0.8rem' }}>
              You can select multiple options:
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1.5,
              mb: 2 
            }}>
              {options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={selectedOptions.includes(option)}
                      onChange={(e) => handleOptionChange(option, e.target.checked)}
                      sx={{ 
                        color: '#1976d2',
                        '&.Mui-checked': {
                          color: '#1976d2',
                        },
                        mr: 0.5
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                      {option}
                    </Typography>
                  }
                  sx={{ 
                    mr: 0,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    border: '1px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.04)',
                      border: '1px solid rgba(25, 118, 210, 0.2)',
                    },
                    ...(selectedOptions.includes(option) && {
                      bgcolor: 'rgba(25, 118, 210, 0.08)',
                      border: '1px solid rgba(25, 118, 210, 0.3)',
                      fontWeight: 500,
                    })
                  }}
                />
              ))}
            </Box>
          </Box>
        ) : (
          <FormControl component="fieldset">
            <RadioGroup
              value={selectedOptions[0] || ''}
              onChange={(e) => handleSingleSelection(e.target.value)}
              sx={{ gap: 1 }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 1.5,
                mb: 2 
              }}>
                {options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={
                      <Radio
                        sx={{ 
                          color: '#1976d2',
                          '&.Mui-checked': {
                            color: '#1976d2',
                          },
                          mr: 0.5
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                        {option}
                      </Typography>
                    }
                    sx={{
                      mr: 0,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      border: '1px solid transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(25, 118, 210, 0.04)',
                        border: '1px solid rgba(25, 118, 210, 0.2)',
                      },
                      ...(selectedOptions[0] === option && {
                        bgcolor: 'rgba(25, 118, 210, 0.08)',
                        border: '1px solid rgba(25, 118, 210, 0.3)',
                        fontWeight: 500,
                      })
                    }}
                  />
                ))}
              </Box>
            </RadioGroup>
          </FormControl>
        )}

        {showCustomInput && (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Please specify"
              value={customText}
              onChange={(e) => {
                const newCustomText = e.target.value;
                setCustomText(newCustomText);
                // Notify parent about text change
                const canSubmit = selectedOptions.length > 0 && 
                  (!showCustomInput || Boolean(newCustomText.trim()));
                onSelectionChange(selectedOptions, newCustomText, canSubmit);
              }}
              placeholder="Describe what you've noticed..."
              multiline
              rows={2}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Box>
        )}

        {selectedOptions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block', fontSize: '0.8rem' }}>
              Selected:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedOptions.map((option, index) => (
                <Chip
                  key={index}
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                  onDelete={() => handleOptionChange(option, false)}
                  sx={{ fontSize: '0.8rem' }}
                />
              ))}
            </Box>
          </Box>
        )}


      </Box>
    </Fade>
  );
};

export default InlineQuestionOptions; 